import { Constants } from "../../../constants";

export const GA_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_ID || "";
export const FRAME_ID = "public-landing-measurement";
const HOST = "achizitii.clpstudio.ro";
const TAG_ID = "public-google-tag";
const COOKIE_SECONDS = 180 * 24 * 60 * 60;
const ENGAGEMENT_INTERVAL_MS = 10000;
export const PENDING_DEMO_KEY = "public-guide-demo-navigation";
const PENDING_DEMO_VERSION = 1;
const PENDING_DEMO_TTL_MS = 30000;
const PENDING_DEMO_MAX_LENGTH = 2048;
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
];
const LABELS = new Set([
  "demo_navigation",
  "demo_calendar",
  "sales_email",
  "sales_phone",
  "platform_preview",
]);
const CTA_EVENTS = new Set([
  "book_demo_click",
  "email_click",
  "phone_click",
  "view_platform_click",
]);
const states = new WeakMap();
const tagStates = new WeakMap();
const FRAME_URL = "/public-landing-measurement.html";
export const PUBLIC_PAGES = Object.freeze({
  "/": { title: "Achiziții Publice — CLP Studio", id: "landing" },
  "/ghiduri.html": {
    title: "Ghiduri pentru instituții publice — CLP Studio",
    id: "guides",
  },
  "/referat-de-necesitate-institutii-publice.html": {
    title: "Referatul de necesitate în instituțiile publice",
    id: "referat_legislatie",
  },
  "/model-referat-de-necesitate.html": {
    title: "Model de referat de necesitate",
    id: "model_referat",
  },
  "/circuit-aprobare-referate-de-necesitate.html": {
    title: "Circuitul de aprobare al referatului de necesitate",
    id: "circuit_aprobare",
  },
  "/referat-de-necesitate-paap.html": {
    title: "Referatul de necesitate și PAAP",
    id: "referat_paap",
  },
  "/referat-de-necesitate-online.html": {
    title: "Referat de necesitate online",
    id: "referat_online",
  },
  "/software-achizitii-publice-institutii.html": {
    title: "Software de achiziții pentru instituții publice",
    id: "software_institutii",
  },
  "/semnatura-electronica-calificata-institutii-publice.html": {
    title: "Semnătura electronică în instituțiile publice",
    id: "semnatura_electronica",
  },
});
const DOWNLOAD_ASSETS = Object.freeze({
  "/resurse/model-referat-de-necesitate.docx": {
    id: "model_referat_docx",
    extension: "docx",
  },
  "/resurse/model-referat-de-necesitate.pdf": {
    id: "model_referat_pdf",
    extension: "pdf",
  },
});
const hasPublicPage = (pathname) =>
  Object.prototype.hasOwnProperty.call(PUBLIC_PAGES, pathname);
const hasDownloadAsset = (pathname) =>
  Object.prototype.hasOwnProperty.call(DOWNLOAD_ASSETS, pathname);
const validUtm = (value) =>
  typeof value === "string" &&
  /^[a-z0-9][a-z0-9_.-]{0,99}$/i.test(value) &&
  !/\d{7,}/.test(value);

function clearPendingDemo(win) {
  try {
    win.sessionStorage.removeItem(PENDING_DEMO_KEY);
  } catch (_) {
    // Storage may be unavailable; never interrupt navigation for analytics.
  }
}

export function isAnonymousLanding(win = window) {
  return win.location.pathname === "/" && isAnonymousPublicPage(win);
}

export function isAnonymousPublicPage(win = window) {
  try {
    const localDebug =
      ["localhost", "127.0.0.1"].includes(win.location.hostname) &&
      new URLSearchParams(win.location.search).get("analytics_debug") === "1";
    return (
      (win.location.hostname === HOST || localDebug) &&
      hasPublicPage(win.location.pathname) &&
      win.localStorage.getItem(Constants.STORAGE_JWT_TOKEN_KEY) === null
    );
  } catch (_) {
    // If authentication storage cannot be checked, do not measure this visitor.
    return false;
  }
}

function stateFor(win) {
  if (!states.has(win))
    states.set(win, {
      frame: null,
      cleanup: [],
      started: false,
      bookings: new Set(),
    });
  return states.get(win);
}

export function stopPublicMeasurement(win = window) {
  if (!isAnonymousPublicPage(win)) clearPendingDemo(win);
  const state = states.get(win);
  if (!state) return;
  if (state.frame) {
    const tagWindow = state.frame.contentWindow;
    if (tagWindow) {
      tagWindow["ga-disable-" + GA_ID] = true;
      if (tagWindow.dataLayer) tagWindow.dataLayer.length = 0;
    }
    state.frame.remove();
    state.frame = null;
  }
  state.started = false;
  if (state.queue) state.queue.length = 0;
  state.cleanup.splice(0).forEach((cleanup) => cleanup());
}

function attachQueue(tagWindow, state) {
  tagWindow.dataLayer = state.queue;
  tagWindow.gtag = function () {
    state.queue.push(arguments);
  };
}

export function publicTagWindow(win = window) {
  if (!isAnonymousPublicPage(win)) {
    stopPublicMeasurement(win);
    return null;
  }
  const state = stateFor(win);
  if (state.frame && state.frame.isConnected) {
    const tagWindow = state.frame.contentWindow;
    if (!tagWindow.gtag) attachQueue(tagWindow, state);
    return tagWindow;
  }
  const frame = win.document.createElement("iframe");
  frame.id = FRAME_ID;
  frame.hidden = true;
  frame.title = "Public landing measurement";
  frame.setAttribute("aria-hidden", "true");
  frame.referrerPolicy = "no-referrer";
  frame.src = FRAME_URL;
  state.queue = [];
  state.ready = false;
  state.pendingTag = null;
  state.pagePath = win.location.pathname;
  frame.addEventListener("load", () => {
    if (state.frame !== frame) return;
    if (!isAnonymousPublicPage(win)) {
      stopPublicMeasurement(win);
      return;
    }
    const loadedWindow = frame.contentWindow;
    attachQueue(loadedWindow, state);
    state.ready = true;
    if (state.pendingTag) loadGoogleTag(loadedWindow, state.pendingTag);
  });
  win.document.body.appendChild(frame);
  state.frame = frame;
  // A separate browsing context destroys all Google timers/listeners on exit.
  // No Google script or dataLayer is installed in the application window.
  const tagWindow = frame.contentWindow;
  attachQueue(tagWindow, state);
  tagStates.set(tagWindow, state);
  const guard = () => {
    if (!isAnonymousPublicPage(win) || state.pagePath !== win.location.pathname)
      stopPublicMeasurement(win);
  };
  ["popstate", "storage", "visibilitychange"].forEach((name) => {
    win.addEventListener(name, guard);
    state.cleanup.push(() => win.removeEventListener(name, guard));
  });
  const leave = () => stopPublicMeasurement(win);
  win.addEventListener("pagehide", leave);
  state.cleanup.push(() => win.removeEventListener("pagehide", leave));
  ["pushState", "replaceState"].forEach((name) => {
    const original = win.history[name];
    const wrapped = function (data, unused, url) {
      if (url && new URL(url, win.location.href).pathname !== state.pagePath) {
        if (!hasPublicPage(new URL(url, win.location.href).pathname))
          clearPendingDemo(win);
        stopPublicMeasurement(win);
      }
      const result = original.apply(this, arguments);
      guard();
      return result;
    };
    win.history[name] = wrapped;
    state.cleanup.push(() => {
      if (win.history[name] === wrapped) win.history[name] = original;
    });
  });
  const beforeClick = (event) => {
    const anchor = event.target.closest && event.target.closest("a[href]");
    if (!anchor || (anchor.target && anchor.target !== "_self")) return;
    const url = new URL(anchor.href, win.location.href);
    if (
      url.origin === win.location.origin &&
      !hasPublicPage(url.pathname) &&
      !hasDownloadAsset(url.pathname)
    ) {
      clearPendingDemo(win);
      leave();
    }
  };
  win.document.addEventListener("click", beforeClick, true);
  state.cleanup.push(() =>
    win.document.removeEventListener("click", beforeClick, true),
  );
  return tagWindow;
}

export function loadGoogleTag(tagWindow, id) {
  const state = tagStates.get(tagWindow);
  if (state && !state.ready) {
    if (!state.pendingTag) state.pendingTag = id;
    return;
  }
  if (tagWindow.document.getElementById(TAG_ID)) return;
  const script = tagWindow.document.createElement("script");
  script.id = TAG_ID;
  script.async = true;
  script.referrerPolicy = "no-referrer";
  script.src = "https://www.googletagmanager.com/gtag/js?id=" + id;
  tagWindow.document.head.appendChild(script);
}

export function landingContext(win = window) {
  const page = hasPublicPage(win.location.pathname)
    ? PUBLIC_PAGES[win.location.pathname]
    : PUBLIC_PAGES["/"];
  const url = new URL(
    hasPublicPage(win.location.pathname) ? win.location.pathname : "/",
    win.location.origin,
  );
  const params = new URLSearchParams(win.location.search);
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (validUtm(value)) url.searchParams.set(key, value);
  });
  let referrer = "";
  try {
    const ref = new URL(win.document.referrer);
    if (["http:", "https:"].includes(ref.protocol)) referrer = ref.origin + "/";
  } catch (_) {
    /* No referring website. */
  }
  return {
    page_location: url.href,
    page_referrer: referrer,
    page_title: page.title,
    content_id: page.id,
  };
}

function send(name, params, win) {
  const state = states.get(win);
  if (!state || !state.started) return false;
  const tagWindow = publicTagWindow(win);
  if (!tagWindow || win["ga-disable-" + GA_ID] === true) return false;
  tagWindow.gtag("event", name, {
    ...landingContext(win),
    ...params,
    send_to: GA_ID,
  });
  return true;
}

function queueDemoNavigation(event, anchor, win) {
  const destination = new URL(anchor.href, win.location.href);
  const target =
    anchor.target ||
    win.document.querySelector("base[target]")?.getAttribute("target") ||
    "_self";
  const state = states.get(win);
  if (
    win.location.pathname === "/" ||
    destination.origin !== win.location.origin ||
    destination.pathname !== "/" ||
    destination.search !== "" ||
    destination.hash !== "#contact" ||
    target !== "_self" ||
    anchor.hasAttribute("download") ||
    event.defaultPrevented ||
    !event.cancelable ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    !state?.started ||
    win["ga-disable-" + GA_ID] === true ||
    !isAnonymousPublicPage(win)
  )
    return false;

  const context = landingContext(win);
  const utm = Object.fromEntries(new URL(context.page_location).searchParams);
  try {
    // Native navigation is immediate. Only this fixed event crosses documents;
    // the source frame can disappear before Google has processed its queue.
    win.sessionStorage.setItem(
      PENDING_DEMO_KEY,
      JSON.stringify({
        version: PENDING_DEMO_VERSION,
        timestamp: Date.now(),
        sourcePath: win.location.pathname,
        utm,
        referrerOrigin: context.page_referrer,
      }),
    );
    return true;
  } catch (_) {
    clearPendingDemo(win);
    return false;
  }
}

function consumePendingDemo(win) {
  if (!isAnonymousLanding(win)) return;
  try {
    const raw = win.sessionStorage.getItem(PENDING_DEMO_KEY);
    // Remove before parsing or sending so retries cannot replay the event.
    win.sessionStorage.removeItem(PENDING_DEMO_KEY);
    if (!raw || raw.length > PENDING_DEMO_MAX_LENGTH) return;
    const pending = JSON.parse(raw);
    const keys = [
      "version",
      "timestamp",
      "sourcePath",
      "utm",
      "referrerOrigin",
    ];
    if (
      !pending ||
      typeof pending !== "object" ||
      Object.keys(pending).length !== keys.length ||
      !keys.every((key) =>
        Object.prototype.hasOwnProperty.call(pending, key),
      ) ||
      pending.version !== PENDING_DEMO_VERSION ||
      !Number.isSafeInteger(pending.timestamp) ||
      pending.timestamp > Date.now() ||
      Date.now() - pending.timestamp > PENDING_DEMO_TTL_MS ||
      typeof pending.sourcePath !== "string" ||
      pending.sourcePath === "/" ||
      !hasPublicPage(pending.sourcePath) ||
      !pending.utm ||
      typeof pending.utm !== "object" ||
      Array.isArray(pending.utm) ||
      !Object.entries(pending.utm).every(
        ([key, value]) => UTM_KEYS.includes(key) && validUtm(value),
      ) ||
      typeof pending.referrerOrigin !== "string"
    )
      return;
    if (pending.referrerOrigin) {
      const referrer = new URL(pending.referrerOrigin);
      if (
        !["http:", "https:"].includes(referrer.protocol) ||
        pending.referrerOrigin !== referrer.origin + "/"
      )
        return;
    }
    const source = new URL(pending.sourcePath, win.location.origin);
    UTM_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(pending.utm, key))
        source.searchParams.set(key, pending.utm[key]);
    });
    const page = PUBLIC_PAGES[pending.sourcePath];
    send(
      "book_demo_click",
      {
        cta_label: "demo_navigation",
        page_location: source.href,
        page_referrer: pending.referrerOrigin,
        page_title: page.title,
        content_id: page.id,
      },
      win,
    );
  } catch (_) {
    clearPendingDemo(win);
  }
}

export function recordLandingBooking(uid, win = window) {
  if (
    !isAnonymousLanding(win) ||
    typeof uid !== "string" ||
    !/^[a-zA-Z0-9_-]{6,100}$/.test(uid)
  )
    return false;
  const state = stateFor(win);
  if (state.bookings.has(uid)) return false;
  if (
    !send(
      "generate_lead",
      { method: "cal_com", lead_type: "demo_booking" },
      win,
    )
  )
    return false;
  state.bookings.add(uid);
  return true;
}

export function startPublicAnalytics(win = window) {
  if (
    !/^G-[A-Z0-9]+$/.test(GA_ID) ||
    win["ga-disable-" + GA_ID] === true ||
    !isAnonymousPublicPage(win)
  ) {
    clearPendingDemo(win);
    return;
  }
  const state = stateFor(win);
  if (state.started) return;
  const tagWindow = publicTagWindow(win);
  state.started = true;
  tagWindow.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  tagWindow.gtag("js", new Date());
  tagWindow.gtag("config", GA_ID, {
    ...landingContext(win),
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_domain: HOST,
    cookie_expires: COOKIE_SECONDS,
    cookie_update: false,
    ...(win.location.hostname !== HOST
      ? { debug_mode: true, cookie_domain: "none" }
      : {}),
  });
  send("page_view", {}, win);
  consumePendingDemo(win);
  loadGoogleTag(tagWindow, GA_ID);
  const click = (event) => {
    const target =
      event.target.closest &&
      event.target.closest("[data-analytics-event], [data-public-event]");
    const eventName =
      target && (target.dataset.publicEvent || target.dataset.analyticsEvent);
    const label =
      target && (target.dataset.publicLabel || target.dataset.analyticsLabel);
    const isGuide = win.location.pathname !== "/";
    if (
      target &&
      (isGuide || target.closest(".lp")) &&
      CTA_EVENTS.has(eventName) &&
      LABELS.has(label)
    ) {
      const params = { cta_label: label };
      const demoAnchor = target.closest("a[href]");
      const queued =
        eventName === "book_demo_click" &&
        label === "demo_navigation" &&
        demoAnchor &&
        queueDemoNavigation(event, demoAnchor, win);
      if (!queued) send(eventName, params, win);
    }
    const anchor =
      event.target.closest &&
      event.target.closest(isGuide ? "a[href]" : ".lp a[href]");
    if (!anchor) return;
    const url = new URL(anchor.href, win.location.href);
    if (!["http:", "https:"].includes(url.protocol)) return;
    const link = { link_url: url.origin + "/", link_domain: url.hostname };
    const asset =
      url.origin === win.location.origin && hasDownloadAsset(url.pathname)
        ? DOWNLOAD_ASSETS[url.pathname]
        : null;
    if (asset)
      send(
        "file_download",
        { ...link, file_extension: asset.extension, asset_id: asset.id },
        win,
      );
    if (url.hostname !== win.location.hostname)
      send("click", { ...link, outbound: true }, win);
  };
  let scrolled = false;
  const scroll = () => {
    const height = win.document.documentElement.scrollHeight;
    if (
      !scrolled &&
      height > win.innerHeight &&
      (win.scrollY + win.innerHeight) / height >= 0.9
    ) {
      scrolled = send("scroll", { percent_scrolled: 90 }, win);
    }
  };
  win.document.addEventListener("click", click);
  win.addEventListener("scroll", scroll, { passive: true });
  // The hidden frame cannot measure focus on the visible landing page itself.
  // Report only time spent on the focused, visible public page; cleanup cancels it.
  const focused = () => !win.document.hidden && win.document.hasFocus();
  let lastTick = Date.now();
  let wasFocused = focused();
  let engagementMs = 0;
  const accrueEngagement = () => {
    const now = Date.now();
    if (wasFocused) engagementMs += Math.max(0, now - lastTick);
    lastTick = now;
    wasFocused = focused();
  };
  const engagementTimer = win.setInterval(() => {
    accrueEngagement();
    if (
      engagementMs > 0 &&
      send("user_engagement", { engagement_time_msec: engagementMs }, win)
    )
      engagementMs = 0;
  }, ENGAGEMENT_INTERVAL_MS);
  ["focus", "blur", "visibilitychange"].forEach((name) => {
    win.addEventListener(name, accrueEngagement);
    state.cleanup.push(() => win.removeEventListener(name, accrueEngagement));
  });
  state.cleanup.push(
    () => win.clearInterval(engagementTimer),
    () => win.document.removeEventListener("click", click),
    () => win.removeEventListener("scroll", scroll),
  );
}
